import {
  baseVendorApiSettingsDefaults,
  defaultHttpAgentSettings,
  defaultRateLimitationSettings,
  defaultSessionSettings,
  defaultVendorApiHttpAdvancedSettings,
} from './base';
import type { ArubaNewCentralApiSettingsDefaults } from './types';

/**
 * Fixed HPE cloud SSO token endpoint used by the `central` auth flow.
 * Used for "Central" auth flow.
 */
export const arubaNewCentralDefaultTokenUrl =
  'https://sso.common.cloud.hpe.com/as/token.oauth2';

/**
 * Fixed base URL of the HPE GreenLake global API. It serves both
 *   * the authorization API
 *   * the workspace-management API — used by the `msp` flow to enumerate the
 *     managed tenants (`workspaces/v1/msp-tenants`).
 *
 * Used in GLP and MSP auth flows.
 */
export const arubaNewCentralGreenlakeBaseUrl =
  'https://global.api.greenlake.hpe.com';

export const arubaNewCentralDefaults: ArubaNewCentralApiSettingsDefaults = {
  ...baseVendorApiSettingsDefaults,
  ...defaultHttpAgentSettings,
  ...defaultRateLimitationSettings,
  ...defaultSessionSettings,
  ...defaultVendorApiHttpAdvancedSettings,
  // Conservative default: 10 tokens / 1000 ms => 10 req/sec, burst of 10.
  maxCapacity: 10,
  refillRate: 10,
  maxConcurrentRequests: 10,
  // New Central (Central-scoped) access tokens are valid for ~2 hours
  defaultSessionTimeoutSec: 2 * 60 * 60,
};

/**
 * Overrides applied on top of {@link arubaNewCentralDefaults} by the GreenLake
 * client. GreenLake is a DIFFERENT host with its own published budget of 1000
 * requests/minute, unrelated to the Central gateway's.
 *
 * Not user-configurable: GreenLake traffic is token mints plus one tenant
 * listing, so there is nothing to tune.
 */
export const arubaNewCentralGreenlakeOverrides = {
  maxCapacity: 16,
  refillRate: 16,
  refillRateIntervalMs: 1000,
  maxConcurrentRequests: 10,
  // GLP/MSP tokens are valid for ~15 minutes, not Central's ~2 hours.
  defaultSessionTimeoutSec: 15 * 60,
} satisfies Partial<ArubaNewCentralApiSettingsDefaults>;
